import { rate, REASONS, type Reason, type Tally } from '@day';

/**
 * Analytics as a CSV: a row per day and a row per courier for the period chosen, the numbers the page shows. Numbers only:
 * no address, customer or stop in it. Kept apart from the page so it can be tested.
 */

const REASON_KEYS = Object.keys(REASONS) as Reason[];

const pct = (n: number, of: number) => {
  const r = rate(n, of);
  return r == null ? '' : `${Math.round(r * 100)}%`;
};

/** Minor units (kobo) as a plain number a spreadsheet can add up: no symbol, no thousands comma. */
const major = (minor: number) => (minor % 100 ? (minor / 100).toFixed(2) : String(minor / 100));

/** A cell, quoted when it must be; a name can't run as a formula when a spreadsheet opens it. */
function cell(v: string): string {
  const safe = /^[=+\-@\t\r]/.test(v) ? `'${v}` : v;
  return /[",\r\n]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe;
}

const numbers = (t: Tally) => [
  String(t.delivered),
  String(t.notDelivered),
  pct(t.firstTime, t.rated),
  pct(t.onTimeMet, t.onTimeOf),
  major(t.collected),
  major(t.cash),
  major(t.transfer),
  major(t.pos),
  ...REASON_KEYS.map((r) => String(t.reasons[r] ?? 0)),
];

export function reportCsv(o: { days: { day: string; tally: Tally }[]; couriers: { name: string; tally: Tally }[]; currency: string }): string {
  const c = o.currency;
  const rows = [
    ['Row', 'Day or courier', 'Delivered', 'Not delivered', 'Delivered first time', 'On time', `Collected at the door (${c})`, `Cash (${c})`, `Transfer (${c})`, `POS (${c})`, ...REASON_KEYS.map((r) => `Not delivered: ${REASONS[r]}`)],
    ...o.days.map((d) => ['Day', d.day, ...numbers(d.tally)]),
    ...o.couriers.map((x) => ['Courier', x.name, ...numbers(x.tally)]),
  ];
  // The mark at the start tells Excel it's UTF-8, for names in any alphabet.
  return `\uFEFF${rows.map((r) => r.map(cell).join(',')).join('\r\n')}\r\n`;
}

/** "Ali Express Analytics 2026-10-27 to 2026-11-02.csv". */
export function reportFileName(business: string | null | undefined, from: string, to: string): string {
  const name = (business ?? '').replace(/[^\p{L}\p{N} _-]/gu, ' ').replace(/\s+/g, ' ').trim();
  return `${name ? `${name} ` : ''}Analytics ${from} to ${to}.csv`;
}
